import { useCallback, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  useRecalculateStrategy,
  useIndicatorKey,
  type PortfolioConstructionType,
  type PortfolioType,
  type RecalculateStrategy,
} from "@repo/api";
import { useToast, useBusy, useTranslation, type Period } from "@repo/core";
import { usePeriod } from "../../../hooks/indicators/usePeriod";
import { useRankingRule, type RankingRuleType } from "../../../hooks/indicators/useRankingRule";
import { usePortfolioActions } from "./usePortfolioActions";
import { useNavigate } from "react-router-dom";
import { usePortfolioQueryModifications } from "./usePortfolioQueryModifications";
import type { StrategyDetailType } from "../../../hooks/useStrategyDetailType";

export const useRecalculatePortfolio = ({
  strategy,
  pageType,
  initialCapital,
  backtestingPeriod,
  numberOfStocks,
  portfolioConstruction,
  leverage,
  rankingRule,
  dateRange,
}: {
  strategy: PortfolioType;
  pageType: StrategyDetailType;
  initialCapital: number;
  backtestingPeriod: string | null;
  numberOfStocks: number;
  portfolioConstruction: PortfolioConstructionType;
  leverage: number;
  rankingRule: RankingRuleType;
  dateRange: Period;
}) => {
  const { getPeriodStart } = usePeriod();
  const { convertRankingRuleToAscending } = useRankingRule(strategy);
  const { universe } = useIndicatorKey();
  const { clonePortfolio, isCloning } = usePortfolioActions();
  const { emptyData, flushData } = usePortfolioQueryModifications();
  const recalculateStrategy = useRecalculateStrategy(strategy.modelId);
  const { setBusy } = useBusy();
  const { addInfoMessage, addErrorMessage } = useToast();
  const { tStrategy } = useTranslation();
  const [showSlotSelectionModal, setShowSlotSelectionModal] = useState(false);

  const navigate = useNavigate();
  const buildRecalculationParams = useCallback((): RecalculateStrategy => {
    return {
      startCash: initialCapital,
      period: backtestingPeriod as string,
      numberOfStocks: numberOfStocks,
      portfolioConstruction: portfolioConstruction,
      leverage: leverage,
      ascending: convertRankingRuleToAscending(
        portfolioConstruction,
        rankingRule
      ),
      backtestPeriod: getPeriodStart(backtestingPeriod as Period),
      longsShare:
        portfolioConstruction === "ShortOnly"
          ? 0
          : portfolioConstruction === "LongShort"
          ? 0.5
          : 1,
      shortsShare:
        portfolioConstruction === "LongOnly"
          ? 0
          : portfolioConstruction === "LongShort"
          ? 0.5
          : 1,
    };
  }, [
    initialCapital,
    numberOfStocks,
    portfolioConstruction,
    leverage,
    rankingRule,
    backtestingPeriod,
    convertRankingRuleToAscending,
    getPeriodStart,
  ]);

  const handleRecalculateDelivered = useCallback(async () => {
    const recalculateParams = buildRecalculationParams();
    const payload = {
      modelId: strategy.modelId,
      universe: universe,
      period: dateRange,
      newModelId: "draft",
      backtest: recalculateParams,
      overwriteExisting: true,
    };
    clonePortfolio({
      payload,
      busyMessage: tStrategy("actions.recalculate.delivered.busy"),
      successMessage: "", // Empty - we'll show info message in customSuccess
      errorMessage: tStrategy("actions.recalculate.delivered.error"),
      preventNavigate: true,
      customSuccess: () => {
        addInfoMessage(tStrategy("actions.backtestingStarted"));
        emptyData("draft");
        flushData("draft");
        navigate(`/draft`);
      },
    });
  }, [
    buildRecalculationParams,
    clonePortfolio,
    navigate,
    emptyData,
    flushData,
    addInfoMessage,
    strategy.modelId,
    universe,
    dateRange,
    tStrategy,
  ]);

  const handleActivePortfolioRecalculate = useCallback(async () => {
    Sentry.addBreadcrumb({
      category: 'portfolio',
      message: 'User initiated active portfolio recalculation',
      level: 'info',
      data: {
        modelId: strategy.modelId,
        pageType,
        initialCapital,
        backtestingPeriod,
        numberOfStocks,
        portfolioConstruction,
        leverage,
        rankingRule,
        universe,
        dateRange,
      },
    });
    const recalculateParams = buildRecalculationParams();
    const payload = {
      modelId: strategy.modelId,
      newModelId: `${strategy.modelId}_draft`,
      backtest: recalculateParams,
      overwriteExisting: true,
    };
    clonePortfolio({
      payload,
      busyMessage: tStrategy("actions.recalculate.active.busy"),
      successMessage: "", // Empty - we'll show info message in customSuccess
      errorMessage: tStrategy("actions.recalculate.active.error"),
      preventNavigate: true,
      customSuccess: (response) => {
        addInfoMessage(tStrategy("actions.backtestingStarted"));
        navigate(`/${response.modelId.replace("_draft", "")}/draft`);
        emptyData(response.modelId);
        flushData(response.modelId);
      },
    });
  }, [
    buildRecalculationParams,
    clonePortfolio,
    navigate,
    emptyData,
    flushData,
    addInfoMessage,
    strategy.modelId,
    tStrategy,
  ]);

  const handleDraftRecalculate = useCallback(async () => {
    Sentry.addBreadcrumb({
      category: 'portfolio',
      message: 'User initiated draft portfolio recalculation',
      level: 'info',
      data: {
        modelId: strategy.modelId,
        pageType,
        initialCapital,
        backtestingPeriod,
        numberOfStocks,
        portfolioConstruction,
        leverage,
        rankingRule,
      },
    });
    const recalculateParams = buildRecalculationParams();
    try {
      setBusy(true, tStrategy("actions.recalculate.draft.busy"));
      await recalculateStrategy.mutateAsync(recalculateParams);
      emptyData(strategy.modelId);
      addInfoMessage(tStrategy("actions.backtestingStarted"));
      setBusy(false);
    } catch (error) {
      console.error("Error recalculating draft portfolio:", error);
      addErrorMessage(tStrategy("actions.recalculate.draft.error"));
      setBusy(false);
    }
  }, [
    strategy.modelId,
    emptyData,
    buildRecalculationParams,
    recalculateStrategy,
    setBusy,
    addInfoMessage,
    addErrorMessage,
    tStrategy,
  ]);

  const handleDraftOnlyRecalculate = useCallback(async () => {
    Sentry.addBreadcrumb({
      category: 'portfolio',
      message: 'User initiated draft-only portfolio recalculation',
      level: 'info',
      data: {
        modelId: strategy.modelId,
        pageType,
        initialCapital,
        backtestingPeriod,
        numberOfStocks,
        portfolioConstruction,
        leverage,
        rankingRule,
      },
    });
    const recalculateParams = buildRecalculationParams();
    try {
      setBusy(true, tStrategy("actions.recalculate.draft.busy"));
      await recalculateStrategy.mutateAsync(recalculateParams);
      emptyData(strategy.modelId);
      addInfoMessage(tStrategy("actions.backtestingStarted"));
      setBusy(false);
    } catch (error) {
      console.error("Error recalculating draft portfolio:", error);
      addErrorMessage(tStrategy("actions.recalculate.draft.error"));
      setBusy(false);
    }
  }, [
    strategy.modelId,
    emptyData,
    buildRecalculationParams,
    recalculateStrategy,
    setBusy,
    addInfoMessage,
    addErrorMessage,
    tStrategy,
  ]);

  const recalculatePortfolio = useCallback(async () => {
    Sentry.addBreadcrumb({
      category: 'portfolio',
      message: 'Portfolio recalculation triggered',
      level: 'info',
      data: {
        pageType,
        modelId: strategy.modelId,
      },
    });
    try {
      switch (pageType) {
        case "delivered":
          await handleRecalculateDelivered();
          break;
        case "active":
          await handleActivePortfolioRecalculate();
          break;
        case "draft":
          await handleDraftRecalculate();
          break;
        case "draft-only":
          await handleDraftOnlyRecalculate();
          break;
        default:
          throw new Error(`Unsupported page type: ${pageType}`);
      }
    } catch (error) {
      console.error("Error during portfolio recalculation:", error);
    }
  }, [
    pageType,
    handleRecalculateDelivered,
    handleActivePortfolioRecalculate,
    handleDraftRecalculate,
    handleDraftOnlyRecalculate,
  ]);

  const handleSlotSelectionModalClose = useCallback(() => {
    setShowSlotSelectionModal(false);
    // setPendingCloneData(null);
  }, []);

  return {
    showSlotSelectionModal,
    handleSlotSelectionModalClose,
    recalculatePortfolio,
    isRecalculating: recalculateStrategy.isPending || isCloning,
  };
};
